/**
 * Wizard Step Constants
 * Named constants for the Master Import Wizard steps.
 * Using constants instead of magic numbers improves code readability and maintainability.
 */

/**
 * Enum for wizard step identifiers.
 * @readonly
 * @enum {number}
 */
export const WIZARD_STEPS = Object.freeze({
  CONFIGURATION: 1,
  COLUMN_MAPPING: 2,
  REVIEW: 3,
  SUCCESS: 4,
});

/**
 * Total number of wizard steps.
 */
export const TOTAL_STEPS = 4;

/**
 * Step metadata for UI display.
 * @type {Array<{step: number, title: string, description: string, icon: string}>}
 */
export const STEP_INFO = Object.freeze([
  {
    step: WIZARD_STEPS.CONFIGURATION,
    title: 'Configure',
    description: 'Setup & upload file',
    icon: 'mdi-cog-outline',
  },
  {
    step: WIZARD_STEPS.COLUMN_MAPPING,
    title: 'Map',
    description: 'Match columns',
    icon: 'mdi-link-variant',
  },
  {
    step: WIZARD_STEPS.REVIEW,
    title: 'Review',
    description: 'Confirm import',
    icon: 'mdi-eye-check-outline',
  },
  {
    step: WIZARD_STEPS.SUCCESS,
    title: 'Complete',
    description: 'Import finished',
    icon: 'mdi-check-circle-outline',
  },
]);

/**
 * Get step info by step number.
 * @param {number} stepNumber - The step number to look up
 * @returns {Object|undefined} Step info object or undefined if not found
 */
export function getStepInfo(stepNumber) {
  return STEP_INFO.find(s => s.step === stepNumber);
}

/**
 * Check if a step number is valid.
 * @param {number} stepNumber - The step number to validate
 * @returns {boolean} True if valid step number
 */
export function isValidStep(stepNumber) {
  return stepNumber >= WIZARD_STEPS.CONFIGURATION && stepNumber <= WIZARD_STEPS.SUCCESS;
}

/**
 * Calculate progress percentage based on current step.
 * @param {number} currentStep - Current wizard step
 * @returns {number} Progress percentage (0-100)
 */
export function calculateProgress(currentStep) {
  if (currentStep < WIZARD_STEPS.CONFIGURATION) return 0;
  if (currentStep >= WIZARD_STEPS.SUCCESS) return 100;
  return Math.round(((currentStep - 1) / (TOTAL_STEPS - 1)) * 100);
}

export default {
  WIZARD_STEPS,
  TOTAL_STEPS,
  STEP_INFO,
  getStepInfo,
  isValidStep,
  calculateProgress,
};
